import type { ComponentProps, ReactNode } from 'react';
import type { To } from 'react-router';
import React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import { Icon } from '../../assets/Icon/Icon';
import { BadgeGroup } from '../../components/Badge/BadgeGroup';
import { selectors } from '../../controls/shared/styles';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Small } from '../../text/Small';

const slotWidth = 32;
export const slotHeight = 44;
const slotMutedOpacity = 0.3;

const slotZIndices = {
  number: 1,
  diagram: 2,
  connection: 3,
  target: 4,
  badge: 5,
};

const SlotConnectionIcon = styled(Icon, {
  width: '$16',
  height: '$16',
  color: colors.iconNeutralLight,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },

  variants: {
    selected: {
      true: {
        color: colors.bodyBrandLight,

        [darkThemeSelector]: {
          color: colors.bodyBrandDark,
        },
      },
      false: {},
    },
  },
});
SlotConnectionIcon.displayName = 'SlotConnectionIcon';

const SlotConnectionLabel = styled(Small, {
  truncate: true,
  width: '100%',
  minWidth: '$40',
  color: colors.headingNeutralLight,
  textAlign: 'center',

  [darkThemeSelector]: {
    color: colors.headingNeutralDark,
  },

  variants: {
    selected: {
      true: {
        color: colors.headingBrandLight,

        [darkThemeSelector]: {
          color: colors.headingBrandDark,
        },
      },
      false: {},
    },
  },
});
SlotConnectionLabel.displayName = 'SlotConnectionLabel';

const SlotConnectionPosition = styled('div', {
  position: 'relative',
  zIndex: 4,
  vStack: '$0',
  alignItems: 'center',
  justifyContent: 'center',
  width: '$48',
  height: '$48',
  padding: '0 $4',
  backgroundColor: colors.bgApplicationLight,
  borderRadius: '$8',
  boxShadow: shadows.connectionLight,

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.connectionDark,
  },

  [selectors.focus]: {
    boxShadow: shadows.focusRingLight,
  },

  variants: {
    selected: {
      true: {
        backgroundColor: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
        },
      },
      false: {},
    },
  },
});
SlotConnectionPosition.displayName = 'SlotConnectionPosition';

const SlotConnectionChain = styled('div', {
  position: 'relative',
  zIndex: 3,
  width: '$2',
  height: '$8',
  marginLeft: '-$1',
  backgroundColor: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.strokeNeutralDark,
  },
});
SlotConnectionChain.displayName = 'SlotConnectionChain';

const SlotConnectionContainer = styled('div', {
  position: 'absolute',
  zIndex: 2,
  left: '-$4',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '$40',

  [selectors.hover]: {
    zIndex: 3,

    [`& ${SlotConnectionPosition}`]: {
      width: 'auto',
      maxWidth: '$320',
      height: '$56',
      padding: '0 $8',
    },

    [`& ${SlotConnectionChain}`]: {
      height: '$4',
    },
  },

  [selectors.focus]: {
    zIndex: 2,
    outline: 'none',

    [`& ${SlotConnectionPosition}`]: {
      boxShadow: shadows.focusRingLight,

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },

  variants: {
    orientation: {
      top: {
        top: '-$56',
        flexDirection: 'column',

        [selectors.hover]: {
          top: '-$60',
        },
      },
      bottom: {
        bottom: `-$${slotHeight + 12}`,
        flexDirection: 'column-reverse',

        [selectors.hover]: {
          bottom: '-$60',
        },
      },
    },
  },
});
SlotConnectionContainer.displayName = 'SlotConnectionContainer';

export type SlotConnectionProps = {
  icon: IconName;
  label: React.ReactNode;
  onClick?: (event: any) => void;
  orientation?: 'bottom' | 'top';
  selected?: boolean;
  // Routing
  as?: React.ElementType;
  to?: To;
};

function SlotConnection({
  icon,
  label,
  onClick,
  orientation = 'top',
  selected = false,
  as,
  to,
  ...remaining
}: SlotConnectionProps) {
  const isPressable = !!(as || to || onClick);

  return (
    <SlotConnectionContainer
      as={as}
      to={to}
      onClick={onClick}
      tabIndex={isPressable ? 0 : undefined}
      role={isPressable && 'button'}
      orientation={orientation}
      {...remaining}
    >
      <SlotConnectionPosition selected={selected}>
        {icon && <SlotConnectionIcon selected={selected} icon={icon} />}
        <SlotConnectionLabel selected={selected}>{label}</SlotConnectionLabel>
      </SlotConnectionPosition>
      <SlotConnectionChain />
    </SlotConnectionContainer>
  );
}

const SlotNumber = styled('span', {
  zIndex: slotZIndices.number,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 $2',
  background: colors.bgNeutralLight,
  borderRadius: '$4',
  color: colors.bodyNeutralLight,
  fontFamily: '$mono',
  fontSize: '$10',
  lineHeight: '$12',

  [darkThemeSelector]: {
    background: colors.bgNeutralDark,
    color: colors.bodyNeutralDark,
  },

  variants: {
    selected: {
      true: {
        background: colors.bgBrandLight,
        color: colors.bodyBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          color: colors.bodyBrandDark,
        },
      },
      false: {},
    },
  },
});
SlotNumber.displayName = 'SlotNumber';

const SlotDiagram = styled('div', {
  zIndex: slotZIndices.diagram,
  width: `${slotWidth}`,
  maxWidth: `${slotWidth}`,
  minWidth: `${slotWidth}`,
  height: '$28',
  maxHeight: '$28',
  minHeight: '$28',
});
SlotDiagram.displayName = 'SlotDiagram';

const SlotTarget = styled('div', {
  position: 'relative',
  zIndex: slotZIndices.target,
  display: 'flex',
  gap: '$4',

  '&::before': {
    content: '',
    position: 'absolute',
    inset: '-$4',
    display: 'block',
    borderRadius: '$8',
  },

  [selectors.focus]: {
    outline: 'none',

    '&::before': {
      boxShadow: shadows.focusRingLight,

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },

  variants: {
    selected: {
      true: {
        '&::before': {
          backgroundColor: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
          },
        },
      },
      false: {},
    },
    orientation: {
      top: {
        flexDirection: 'column-reverse',
      },
      bottom: {
        flexDirection: 'column',
      },
    },
  },
});
SlotTarget.displayName = 'SlotTarget';

const SlotBadge = styled(BadgeGroup, {
  position: 'absolute',
  zIndex: slotZIndices.badge,
  left: '50%',

  variants: {
    orientation: {
      top: {
        bottom: 0,
        transform: 'translate(-50%, 50%)',
      },
      bottom: {
        top: 0,
        transform: 'translate(-50%, -50%)',
      },
    },
  },
});
SlotBadge.displayName = 'SlotBadge';

const SlotContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  width: 'fit-content',
  height: slotHeight,

  [selectors.hover]: {
    [`& ${SlotConnectionContainer}`]: {
      zIndex: slotZIndices.connection,

      [`& ${SlotConnectionPosition}`]: {
        width: 'auto',
        maxWidth: '$320',
        height: '$56',
        padding: '0 $8',
      },

      [`& ${SlotConnectionChain}`]: {
        height: '$4',
      },
    },
  },

  variants: {
    muted: {
      true: {
        opacity: slotMutedOpacity,
      },
      false: {},
    },
    orientation: {
      top: {
        [selectors.hover]: {
          [`& ${SlotConnectionContainer}`]: {
            bottom: '-$60',
          },
        },
      },
      bottom: {
        [selectors.hover]: {
          [`& ${SlotConnectionContainer}`]: {
            top: '-$60',
          },
        },
      },
    },
  },
});
SlotContainer.displayName = 'SlotContainer';

export type SlotOrientation = 'top' | 'bottom';

export type SlotProps = {
  badges?: React.ReactNode;
  connection?: SlotConnectionProps;
  diagram: React.ReactNode;
  label?: string | null;
  muted?: boolean;
  number?: number;
  onClick?: (event: any) => void;
  orientation?: SlotOrientation;
  selected?: boolean;
} & ComponentProps<typeof SlotContainer>;

export function Slot({
  badges,
  connection,
  diagram,
  label,
  muted = false,
  number,
  onClick,
  orientation = 'bottom',
  selected,
  ...remaining
}: SlotProps) {
  return (
    <SlotContainer orientation={orientation} muted={muted} {...remaining}>
      {connection && (
        <SlotConnection orientation={orientation === 'top' ? 'bottom' : 'top'} {...connection} />
      )}
      {badges && (
        <SlotBadge orientation={orientation} relation="joined" size="small">
          {badges}
        </SlotBadge>
      )}
      <SlotTarget
        onClick={onClick}
        tabIndex={onClick && 0}
        role={onClick && 'button'}
        selected={selected}
        orientation={orientation}
      >
        <SlotDiagram>{diagram}</SlotDiagram>
        <SlotNumber selected={selected}>{label ?? number}</SlotNumber>
      </SlotTarget>
    </SlotContainer>
  );
}

const SlotsLayout = styled('div', {
  gap: '$8',
  width: 'fit-content',

  variants: {
    align: {
      top: {},
      bottom: {
        alignSelf: 'flex-end',
      },
    },
    rows: {
      1: {
        display: 'flex',
        flexDirection: 'row',
      },
      2: {
        display: 'grid',
        gridAutoFlow: 'column',
        gridTemplateColumns: `repeat(auto-fit, $${slotWidth})`,
        gridTemplateRows: `repeat(2, $${slotHeight})`,
      },
    },
  },
});
SlotsLayout.displayName = 'SlotsLayout';

const SlotsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
});
SlotsContainer.displayName = 'SlotsContainer';

type SlotsProps = {
  align?: 'top' | 'bottom';
  label?: ReactNode;
  orientation?: 'top' | 'bottom';
  rows?: 1 | 2;
  slots: SlotProps[];
  start?: number;
} & ComponentProps<typeof SlotsContainer>;

export function Slots({
  align,
  label,
  orientation,
  rows = 1,
  slots,
  start = 0,
  ...remaining
}: SlotsProps) {
  return (
    <SlotsContainer {...remaining}>
      <SlotsLayout align={align} rows={rows}>
        {slots.map((slot, index) => {
          const isOdd = index % 2 === 0;
          const key = start + index;
          return (
            <Slot
              key={`slot-${key}`}
              orientation={orientation ?? (rows !== 1 && !isOdd ? 'top' : undefined)}
              number={index + start}
              {...slot}
            />
          );
        })}
      </SlotsLayout>
    </SlotsContainer>
  );
}
